import { Link, useLocation, useSearch } from "wouter";
import { Inbox, Loader2, Menu, Moon, Search, Sun, X } from "lucide-react";
import { useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { useIsFetching } from "@tanstack/react-query";
import type { Product } from "@shared/types";
import { useAuth } from "@/_core/hooks/useAuth";
import {
  getProductCategorySegment,
  getProductPath,
} from "@shared/productRouting";
import { CATALOG_FILTERS, getCatalogFilterPath } from "@/lib/routes";
import { trpc } from "@/lib/trpc";
import { useComposition } from "@/hooks/useComposition";
import { useProductAnalytics } from "@/hooks/useProductAnalytics";
import { useTheme } from "@/contexts/useTheme";
import { cn } from "@/lib/utils";
import { BRAND_LOGO_URL } from "@/lib/brand";
import { toDisplayAssetUrl } from "@/lib/assets";
import { getLoginUrl, getSignupUrl } from "@/const";
import { Button } from "@/components/ui/button";

const SEARCH_RESULTS_LIMIT = 6;
const SEARCH_QUERY_FETCH_LIMIT = 18;
const TRENDING_RESULTS_LIMIT = 5;

const CATEGORY_LABELS: Record<string, string> = {
  phones: "Phones",
  tablets: "Tablets",
  headphones: "Headphones",
  "game-consoles": "Consoles",
  "vr-headsets": "VR",
};

function formatPrice(value: number) {
  return `$${value.toLocaleString()}`;
}

function getCategoryLabel(product: Product) {
  return CATEGORY_LABELS[getProductCategorySegment(product)] ?? "Products";
}

function getSearchScore(product: Product, query: string) {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return 0;

  const name = product.name.toLowerCase();
  const model = product.marketModel.toLowerCase();
  const brand = product.brand.toLowerCase();
  const combinedName = `${brand} ${name}`;
  const combinedModel = `${brand} ${model}`;

  if (name === normalizedQuery || model === normalizedQuery) return 120;
  if (combinedName === normalizedQuery || combinedModel === normalizedQuery)
    return 110;
  if (name.startsWith(normalizedQuery)) return 96;
  if (model.startsWith(normalizedQuery)) return 92;
  if (
    combinedName.startsWith(normalizedQuery) ||
    combinedModel.startsWith(normalizedQuery)
  ) {
    return 88;
  }
  if (brand === normalizedQuery) return 80;
  if (name.includes(normalizedQuery)) return 72;
  if (model.includes(normalizedQuery)) return 68;
  if (brand.startsWith(normalizedQuery)) return 60;
  if (brand.includes(normalizedQuery)) return 48;

  return 0;
}

function preventDefault<T extends { preventDefault: () => void }>(event: T) {
  event.preventDefault();
}

export default function Navbar() {
  const [location, navigate] = useLocation();
  const { theme, toggleTheme } = useTheme();
  const { user, loading: authLoading, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isTopLoading, setIsTopLoading] = useState(false);
  const [topProgress, setTopProgress] = useState(0);
  const progressTicker = useRef<ReturnType<typeof setInterval> | null>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navRef = useRef<HTMLElement | null>(null);
  const searchContainerRef = useRef<HTMLDivElement | null>(null);
  const desktopSearchInputRef = useRef<HTMLInputElement | null>(null);
  const mobileSearchInputRef = useRef<HTMLInputElement | null>(null);
  const lastTrackedSearchKeyRef = useRef("");
  const [navHeight, setNavHeight] = useState(100);
  const { trackProductClick, trackSearchResults } = useProductAnalytics();

  const isFetching = useIsFetching() > 0;
  // wouter's location is the path only; the query string comes from useSearch.
  const searchString = useSearch();
  const urlSearch = useMemo(
    () => new URLSearchParams(searchString).get("search") || "",
    [searchString]
  );
  const deferredSearchQuery = useDeferredValue(searchQuery.trim());
  const hasSearchQuery = deferredSearchQuery.length > 0;
  const searchActive = searchFocused || searchOpen;
  const offerInboxQuery = trpc.marketplace.offerInbox.useQuery(undefined, {
    enabled: Boolean(user),
    retry: false,
    refetchOnWindowFocus: true,
  });
  const unreadOfferCount = useMemo(
    () =>
      [
        ...(offerInboxQuery.data?.incoming ?? []),
        ...(offerInboxQuery.data?.outgoing ?? []),
      ].filter(offer =>
        offer.role === "seller" ? offer.sellerUnread : offer.buyerUnread
      ).length,
    [offerInboxQuery.data?.incoming, offerInboxQuery.data?.outgoing]
  );

  useEffect(() => {
    if (isFetching) {
      if (hideTimer.current) {
        clearTimeout(hideTimer.current);
        hideTimer.current = null;
      }

      setIsTopLoading(true);
      setTopProgress(5);

      if (progressTicker.current) {
        clearInterval(progressTicker.current);
      }

      progressTicker.current = setInterval(() => {
        setTopProgress(value => {
          if (value >= 90) return 90;
          return value + 6;
        });
      }, 90);
      return;
    }

    if (progressTicker.current) {
      clearInterval(progressTicker.current);
      progressTicker.current = null;
    }

    setTopProgress(100);

    hideTimer.current = setTimeout(() => {
      setIsTopLoading(false);
      setTopProgress(0);
    }, 220);
  }, [isFetching]);

  useEffect(() => {
    return () => {
      if (progressTicker.current) {
        clearInterval(progressTicker.current);
      }
      if (hideTimer.current) {
        clearTimeout(hideTimer.current);
      }
    };
  }, []);

  useEffect(() => {
    setSearchQuery(urlSearch);
  }, [urlSearch]);

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [deferredSearchQuery, searchFocused]);

  useEffect(() => {
    if (!searchFocused || !hasSearchQuery) {
      lastTrackedSearchKeyRef.current = "";
    }
  }, [hasSearchQuery, searchFocused]);

  useEffect(() => {
    setSearchFocused(false);
    setHighlightedIndex(-1);
    setSearchOpen(false);
    setMobileMenuOpen(false);
  }, [location]);

  useEffect(() => {
    if (!searchOpen) return;

    const frame = window.requestAnimationFrame(() => {
      mobileSearchInputRef.current?.focus({ preventScroll: true });
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [searchOpen]);

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (!searchContainerRef.current?.contains(event.target as Node)) {
        setSearchFocused(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
    };
  }, []);

  useEffect(() => {
    const node = navRef.current;
    if (!node) return;

    const syncNavHeight = () => {
      setNavHeight(currentHeight => {
        const nextHeight = Math.ceil(node.getBoundingClientRect().height);
        return currentHeight === nextHeight ? currentHeight : nextHeight;
      });
    };

    syncNavHeight();

    const observer = new ResizeObserver(() => {
      syncNavHeight();
    });

    observer.observe(node);
    window.addEventListener("resize", syncNavHeight);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", syncNavHeight);
    };
  }, [mobileMenuOpen, searchOpen, isTopLoading]);

  useEffect(() => {
    if (!mobileMenuOpen) return;

    const scrollY = window.scrollY;
    const { style: bodyStyle } = document.body;
    const { style: docStyle } = document.documentElement;
    const previousMobileMenuFlag = document.body.dataset.mobileMenuOpen;
    const previousBody = {
      overflow: bodyStyle.overflow,
      position: bodyStyle.position,
      top: bodyStyle.top,
      left: bodyStyle.left,
      right: bodyStyle.right,
      width: bodyStyle.width,
    };
    const previousDocOverflow = docStyle.overflow;

    bodyStyle.overflow = "hidden";
    bodyStyle.position = "fixed";
    bodyStyle.top = `-${scrollY}px`;
    bodyStyle.left = "0";
    bodyStyle.right = "0";
    bodyStyle.width = "100%";
    docStyle.overflow = "hidden";
    document.body.dataset.mobileMenuOpen = "true";

    return () => {
      bodyStyle.overflow = previousBody.overflow;
      bodyStyle.position = previousBody.position;
      bodyStyle.top = previousBody.top;
      bodyStyle.left = previousBody.left;
      bodyStyle.right = previousBody.right;
      bodyStyle.width = previousBody.width;
      docStyle.overflow = previousDocOverflow;
      if (previousMobileMenuFlag === undefined) {
        delete document.body.dataset.mobileMenuOpen;
      } else {
        document.body.dataset.mobileMenuOpen = previousMobileMenuFlag;
      }
      window.scrollTo(0, scrollY);
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    if (!searchOpen) return;

    const { style: bodyStyle } = document.body;
    const { style: docStyle } = document.documentElement;
    const previousBody = {
      overflow: bodyStyle.overflow,
      height: bodyStyle.height,
      overscrollBehavior: bodyStyle.overscrollBehavior,
    };
    const previousDoc = {
      overflow: docStyle.overflow,
      height: docStyle.height,
      overscrollBehavior: docStyle.overscrollBehavior,
    };

    bodyStyle.overflow = "hidden";
    bodyStyle.height = "100%";
    bodyStyle.overscrollBehavior = "none";
    docStyle.overflow = "hidden";
    docStyle.height = "100%";
    docStyle.overscrollBehavior = "none";

    return () => {
      bodyStyle.overflow = previousBody.overflow;
      bodyStyle.height = previousBody.height;
      bodyStyle.overscrollBehavior = previousBody.overscrollBehavior;
      docStyle.overflow = previousDoc.overflow;
      docStyle.height = previousDoc.height;
      docStyle.overscrollBehavior = previousDoc.overscrollBehavior;
    };
  }, [searchOpen]);

  const navLinks = CATALOG_FILTERS.map(filter => ({
    label: filter.label,
    href: getCatalogFilterPath(filter.slug),
  }));

  const isActiveLink = (href: string) =>
    location === href ||
    location.startsWith(`${href}/`) ||
    location.startsWith(`${href}?`);

  const navSurfaceClass =
    "fixed inset-x-0 top-0 z-50 border-b border-border bg-white dark:bg-background";
  const logoClass = "flex shrink-0 items-center select-none";
  const mobileMenuClass =
    "fixed inset-x-0 bottom-0 z-[60] overflow-hidden border-t border-border bg-background supports-[backdrop-filter]:bg-background xl:hidden";

  const { data: liveSearchData, isFetching: isSearchFetching } =
    trpc.products.list.useQuery(
      {
        search: deferredSearchQuery,
        sortBy: "sales",
        sortDir: "desc",
        limit: SEARCH_QUERY_FETCH_LIMIT,
        offset: 0,
      },
      {
        enabled: searchActive && hasSearchQuery,
      }
    );

  const { data: trendingProducts } = trpc.products.trending.useQuery(
    { limit: TRENDING_RESULTS_LIMIT },
    {
      enabled: searchActive && !hasSearchQuery,
    }
  );

  const searchResults = useMemo(() => {
    if (!liveSearchData?.items) return [];

    return [...liveSearchData.items]
      .sort((a, b) => {
        const scoreDiff =
          getSearchScore(b, deferredSearchQuery) -
          getSearchScore(a, deferredSearchQuery);
        if (scoreDiff !== 0) return scoreDiff;
        return b.totalSales - a.totalSales;
      })
      .slice(0, SEARCH_RESULTS_LIMIT);
  }, [deferredSearchQuery, liveSearchData?.items]);

  const dropdownProducts = hasSearchQuery
    ? searchResults
    : (trendingProducts ?? []);
  const maxHighlightedIndex = dropdownProducts.length - 1;
  const showSearchDropdown =
    searchActive &&
    (isSearchFetching || dropdownProducts.length > 0 || hasSearchQuery);
  const showNoResults =
    hasSearchQuery && !isSearchFetching && dropdownProducts.length === 0;

  const dismissSearchDropdown = () => {
    setSearchFocused(false);
    setHighlightedIndex(-1);
  };

  const closeSearch = () => {
    dismissSearchDropdown();
    setSearchOpen(false);
  };

  useEffect(() => {
    if (!searchActive || !hasSearchQuery || searchResults.length === 0) {
      return;
    }

    const searchKey = `${deferredSearchQuery}:${searchResults
      .slice(0, 5)
      .map(product => product.id)
      .join(",")}`;

    if (lastTrackedSearchKeyRef.current === searchKey) {
      return;
    }

    lastTrackedSearchKeyRef.current = searchKey;
    trackSearchResults(deferredSearchQuery, searchResults, "navbar-search");
  }, [
    deferredSearchQuery,
    hasSearchQuery,
    searchActive,
    searchFocused,
    searchResults,
    trackSearchResults,
  ]);

  const goToProduct = (product: Product, source?: string) => {
    trackProductClick(
      product.id,
      source ??
        (hasSearchQuery ? "navbar-search-click" : "navbar-trending-click")
    );
    navigate(getProductPath(product));
    dismissSearchDropdown();
  };

  const themeToggleLabel =
    theme === "light" ? "Switch to dark mode" : "Switch to light mode";
  const authCtaClass =
    "hidden h-10 rounded-xl px-3 text-sm font-medium xl:inline-flex";
  const searchDropdownContent = (
    <>
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
            {hasSearchQuery ? "Live Search" : "Trending Now"}
          </p>
          <p className="mt-1 text-sm text-foreground">
            {hasSearchQuery
              ? `Top matches for "${deferredSearchQuery}"`
              : "Popular products people are watching"}
          </p>
        </div>
        {hasSearchQuery ? (
          <p className="hidden text-[11px] font-medium text-muted-foreground md:block">
            Press Enter to open the top match
          </p>
        ) : null}
      </div>

      <div className="max-h-none overflow-y-auto py-2 md:max-h-[28rem]">
        {dropdownProducts.map((product, index) => (
          <button
            key={product.id}
            type="button"
            onMouseDown={event => event.preventDefault()}
            onClick={() => goToProduct(product)}
            onMouseEnter={() => setHighlightedIndex(index)}
            className={`flex w-full items-center gap-3 px-4 py-3 text-left transition-colors ${
              highlightedIndex === index ? "bg-primary/10" : "hover:bg-accent/70"
            }`}
          >
            <div className="product-cutout-frame flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden md:h-14 md:w-14">
              {product.imageUrl ? (
                <img
                  src={toDisplayAssetUrl(product.imageUrl)}
                  alt={product.name}
                  className="product-cutout-image h-full w-full object-contain"
                />
              ) : (
                <span className="text-lg font-semibold text-muted-foreground">
                  {product.brand[0]}
                </span>
              )}
            </div>

            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-foreground">
                {product.marketModel}
              </p>
              <p className="mt-1 truncate text-xs text-muted-foreground">
                {product.brand} · {getCategoryLabel(product)}
              </p>
            </div>

            <div className="shrink-0 text-right">
              <p className="hidden text-[10px] uppercase tracking-[0.16em] text-muted-foreground sm:block">
                Market Price
              </p>
              <p className="mt-1 text-sm font-semibold text-foreground">
                {formatPrice(product.currentPrice)}
              </p>
            </div>
          </button>
        ))}

        {showNoResults ? (
          <div className="px-4 py-8 text-center">
            <p className="text-sm font-medium text-foreground">
              No instant matches yet
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Try a different model, brand, or product name for "
              {deferredSearchQuery}".
            </p>
          </div>
        ) : null}
      </div>
    </>
  );

  const composition = useComposition<HTMLInputElement>({
    onKeyDown: event => {
      if (!showSearchDropdown && event.key === "Enter") {
        if (searchResults[0]) {
          event.preventDefault();
          goToProduct(searchResults[0], "navbar-enter-top-match");
        }
        return;
      }

      if (event.key === "ArrowDown") {
        event.preventDefault();
        if (maxHighlightedIndex < 0) return;
        setHighlightedIndex(current =>
          current >= maxHighlightedIndex ? 0 : current + 1
        );
        return;
      }

      if (event.key === "ArrowUp") {
        event.preventDefault();
        if (maxHighlightedIndex < 0) return;
        setHighlightedIndex(current =>
          current <= 0 ? maxHighlightedIndex : current - 1
        );
        return;
      }

      if (event.key === "Escape") {
        closeSearch();
        return;
      }

      if (event.key !== "Enter") return;

      event.preventDefault();

      if (highlightedIndex >= 0 && highlightedIndex < dropdownProducts.length) {
        goToProduct(
          dropdownProducts[highlightedIndex],
          hasSearchQuery
            ? "navbar-enter-highlighted-search"
            : "navbar-enter-highlighted-trending"
        );
        return;
      }

      if (searchResults[0]) {
        goToProduct(searchResults[0], "navbar-enter-top-match");
      }
    },
  });

  return (
    <>
      <div
        aria-hidden="true"
        className="shrink-0"
        style={{ height: navHeight }}
      />

      <nav ref={navRef} className={navSurfaceClass}>
        <div
          className={`h-0.5 overflow-hidden transition-opacity duration-150 ${isTopLoading ? "opacity-100" : "opacity-0"}`}
        >
          <div
            className="h-full bg-gradient-to-r from-primary via-primary/60 to-transparent transition-all duration-150"
            style={{ width: `${topProgress}%` }}
          />
        </div>

        <div className="container">
          <div className="flex h-16 items-center gap-4 md:grid md:grid-cols-[auto_minmax(0,1fr)_auto] md:gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(18rem,48rem)_minmax(0,1fr)] xl:gap-6">
            <Link
              href="/"
              aria-label="Techfare home"
              className={logoClass}
              draggable={false}
              onContextMenu={preventDefault}
              onCopy={preventDefault}
              onCut={preventDefault}
              onDragStart={preventDefault}
            >
              <span
                aria-hidden="true"
                className="brandmark-protected block h-7 w-[120px] sm:h-8 sm:w-[136px] lg:w-[148px]"
                style={{ backgroundImage: `url("${BRAND_LOGO_URL}")` }}
              />
              <span className="sr-only">Techfare</span>
            </Link>

            <div
              ref={searchContainerRef}
              className="relative hidden min-w-0 flex-1 md:flex md:w-full md:justify-self-center"
            >
              <Search className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                ref={desktopSearchInputRef}
                type="text"
                placeholder="Search technology..."
                value={searchQuery}
                onChange={event => setSearchQuery(event.target.value)}
                onFocus={() => setSearchFocused(true)}
                {...composition}
                className={`h-10 w-52 rounded-xl border bg-white pl-9 pr-11 text-sm text-foreground placeholder:text-muted-foreground transition-all focus:outline-none focus:ring-0 dark:border-white/10 dark:bg-[#050505] dark:text-white dark:placeholder:text-zinc-500 md:w-full ${
                  searchFocused
                    ? "border-primary/60 bg-card shadow-[0_18px_40px_rgba(15,23,42,0.14)] dark:bg-[#0a0a0a] dark:shadow-[0_18px_40px_rgba(0,0,0,0.36)]"
                    : "border-border dark:border-white/10"
                }`}
              />
              {searchQuery ? (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => {
                    setSearchQuery("");
                    setHighlightedIndex(-1);
                    desktopSearchInputRef.current?.focus({ preventScroll: true });
                  }}
                  className="absolute right-2 top-1/2 z-10 inline-flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              ) : isSearchFetching && hasSearchQuery ? (
                <Loader2 className="pointer-events-none absolute right-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
              ) : null}

              {showSearchDropdown ? (
                <div className="absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden rounded-2xl border border-border bg-popover shadow-[0_28px_80px_rgba(15,23,42,0.16)] dark:shadow-[0_28px_80px_rgba(0,0,0,0.48)]">
                  {searchDropdownContent}
                </div>
              ) : null}
            </div>

            <div className="ml-auto flex items-center gap-2 md:ml-0 md:justify-self-end">
              {authLoading ? null : user ? (
                <>
                  <Button
                    asChild
                    variant="ghost"
                    size="sm"
                    className={cn(
                      authCtaClass,
                      "text-foreground hover:bg-accent"
                    )}
                  >
                    <Link href="/sell">Sell</Link>
                  </Button>
                  <Button
                    asChild
                    variant="ghost"
                    size="icon"
                    className="relative hidden h-10 w-10 rounded-xl xl:inline-flex"
                  >
                    <Link
                      href="/account/offers"
                      aria-label="Inbox"
                      title="Inbox"
                    >
                      <Inbox className="h-4 w-4" />
                      {unreadOfferCount > 0 ? (
                        <span className="absolute -right-1 -top-1 inline-flex min-h-5 min-w-5 items-center justify-center rounded-full bg-rose-600 px-1.5 text-[10px] font-semibold text-white">
                          {unreadOfferCount > 99 ? "99+" : unreadOfferCount}
                        </span>
                      ) : null}
                    </Link>
                  </Button>
                  <Button
                    asChild
                    variant="ghost"
                    size="sm"
                    className={cn(
                      authCtaClass,
                      "text-foreground hover:bg-accent"
                    )}
                  >
                    <Link href="/account">Account</Link>
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      void logout();
                    }}
                    className={authCtaClass}
                  >
                    Sign out
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    asChild
                    variant="ghost"
                    size="sm"
                    className={cn(
                      authCtaClass,
                      "text-foreground hover:bg-accent"
                    )}
                  >
                    <a href={getLoginUrl()}>Log in</a>
                  </Button>
                  <Button
                    asChild
                    size="sm"
                    className={cn(
                      authCtaClass,
                      "bg-foreground text-background shadow-sm hover:bg-foreground/90"
                    )}
                  >
                    <a href={getSignupUrl()}>Sign up</a>
                  </Button>
                </>
              )}

              <button
                type="button"
                onClick={() => toggleTheme?.()}
                aria-label={themeToggleLabel}
                title={themeToggleLabel}
                className="hidden h-10 w-10 items-center justify-center rounded-xl border border-border bg-card text-muted-foreground transition-colors hover:bg-accent hover:text-foreground md:inline-flex"
              >
                {theme === "light" ? (
                  <Moon className="h-4 w-4" />
                ) : (
                  <Sun className="h-4 w-4" />
                )}
              </button>

              <button
                type="button"
                onClick={() => {
                  setMobileMenuOpen(false);
                  setSearchOpen(true);
                  setSearchFocused(true);
                }}
                aria-label="Open search"
                title="Open search"
                className="p-2 text-muted-foreground hover:text-foreground md:hidden"
              >
                <Search className="h-5 w-5" />
              </button>

              <button
                type="button"
                onClick={() => {
                  closeSearch();
                  setMobileMenuOpen(!mobileMenuOpen);
                }}
                aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
                aria-expanded={mobileMenuOpen}
                title={mobileMenuOpen ? "Close menu" : "Open menu"}
                className="inline-flex p-2 text-muted-foreground hover:text-foreground xl:hidden"
              >
                {mobileMenuOpen ? (
                  <X className="h-5 w-5" />
                ) : (
                  <Menu className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="border-b border-border/90">
          <div className="container">
            <div className="scrollbar-none flex h-9 items-center overflow-x-auto overflow-y-hidden whitespace-nowrap [touch-action:pan-x] lg:h-11 lg:justify-center">
              <div className="flex min-w-max items-center gap-6 px-1 lg:gap-8">
                {navLinks.map(link => {
                  const isActive = isActiveLink(link.href);

                  return (
                    <Link
                      key={link.href}
                      href={link.href}
                      aria-current={isActive ? "page" : undefined}
                      className={cn(
                        "relative inline-flex h-9 items-center text-[14px] font-medium tracking-[-0.01em] transition-colors duration-200 lg:h-11 lg:text-[15px]",
                        "after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:origin-center after:scale-x-0 after:rounded-full after:bg-black after:transition-transform after:duration-200 hover:after:scale-x-100 dark:after:bg-white",
                        isActive
                          ? "text-black after:h-1 after:scale-x-100 dark:text-white"
                          : "text-black/72 after:scale-x-0 hover:text-black dark:text-white/72 dark:hover:text-white"
                      )}
                    >
                      {link.label}
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

      </nav>

      {searchOpen && (
        <div className="fixed inset-x-0 top-0 z-[70] h-[100dvh] overflow-hidden bg-white dark:bg-background md:hidden">
          <div className="absolute inset-x-0 top-0 z-[80] border-b border-border/90 bg-white pt-[max(env(safe-area-inset-top),0.5rem)] dark:bg-background">
            <div className="container flex items-center gap-3 py-3">
              <div className="relative flex-1">
                <Search className="pointer-events-none absolute left-4 top-1/2 z-10 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
                <input
                  ref={mobileSearchInputRef}
                  type="text"
                  placeholder="Search technology..."
                  value={searchQuery}
                  onChange={event => setSearchQuery(event.target.value)}
                  onFocus={() => setSearchFocused(true)}
                  {...composition}
                  className={`h-12 w-full rounded-2xl border bg-white pl-12 pr-11 text-base text-foreground placeholder:text-muted-foreground transition-all focus:outline-none focus:ring-0 dark:border-white/10 dark:bg-[#050505] dark:text-white dark:placeholder:text-zinc-500 ${
                    searchFocused
                      ? "border-primary/60 bg-card shadow-[0_18px_40px_rgba(15,23,42,0.14)] dark:bg-[#0a0a0a] dark:shadow-[0_18px_40px_rgba(0,0,0,0.36)]"
                      : "border-border dark:border-white/10"
                  }`}
                />
                {searchQuery ? (
                  <button
                    type="button"
                    aria-label="Clear search"
                    onClick={() => {
                      setSearchQuery("");
                      setHighlightedIndex(-1);
                      mobileSearchInputRef.current?.focus({ preventScroll: true });
                    }}
                    className="absolute right-2 top-1/2 z-10 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                ) : isSearchFetching && hasSearchQuery ? (
                  <Loader2 className="pointer-events-none absolute right-4 top-1/2 z-10 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
                ) : null}
              </div>
              <button
                type="button"
                onClick={closeSearch}
                className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-border bg-card text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                aria-label="Close search"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="absolute inset-x-0 bottom-0 top-[calc(env(safe-area-inset-top)+5.75rem)] overflow-y-auto overscroll-contain [touch-action:pan-y] bg-popover">
            {searchDropdownContent}
          </div>
        </div>
      )}

      {mobileMenuOpen && (
        <>
          <button
            type="button"
            aria-label="Close menu"
            className="fixed inset-x-0 bottom-0 z-[55] bg-black/20 backdrop-blur-[1px] xl:hidden"
            style={{ top: navHeight }}
            onClick={() => setMobileMenuOpen(false)}
          />
          <div
            className={mobileMenuClass}
            style={{
              top: navHeight,
              maxHeight: `calc(100dvh - ${navHeight}px)`,
            }}
          >
            <div className="h-full overflow-y-auto overscroll-contain">
              <div className="container space-y-1 py-3 pb-6">
                {!authLoading ? (
                  user ? (
                    <div className="mb-3 rounded-xl border border-border bg-card px-3 py-3">
                      <p className="text-sm font-semibold text-foreground">
                        {user.name || "Signed in"}
                      </p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {user.email || "Your Techfare account"}
                      </p>
                      <div className="mt-3 grid grid-cols-2 gap-2">
                        <Button
                          asChild
                          variant="outline"
                          className="w-full justify-center"
                        >
                          <Link
                            href="/account/offers"
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            <span className="relative inline-flex items-center gap-2">
                              <Inbox className="h-4 w-4" />
                              Inbox
                              {unreadOfferCount > 0 ? (
                                <span className="inline-flex min-h-5 min-w-5 items-center justify-center rounded-full bg-rose-600 px-1.5 text-[10px] font-semibold text-white">
                                  {unreadOfferCount > 99
                                    ? "99+"
                                    : unreadOfferCount}
                                </span>
                              ) : null}
                            </span>
                          </Link>
                        </Button>
                        <Button
                          asChild
                          variant="outline"
                          className="w-full justify-center"
                        >
                          <Link
                            href="/account"
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            My Account
                          </Link>
                        </Button>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          void logout();
                          setMobileMenuOpen(false);
                        }}
                        className="mt-3 w-full justify-center"
                      >
                        Sign out
                      </Button>
                    </div>
                  ) : (
                    <div className="mb-3 grid grid-cols-2 gap-2">
                      <Button
                        asChild
                        variant="outline"
                        className="w-full justify-center"
                      >
                        <a href={getLoginUrl()}>Log in</a>
                      </Button>
                      <Button asChild className="w-full justify-center">
                        <a href={getSignupUrl()}>Sign up</a>
                      </Button>
                    </div>
                  )
                ) : null}

                {navLinks.map(link => (
                  <Link
                    key={link.href}
                    href={link.href}
                    aria-current={isActiveLink(link.href) ? "page" : undefined}
                    onClick={() => setMobileMenuOpen(false)}
                    className={cn(
                      "block border-l-2 px-3 py-2.5 text-sm font-medium transition-colors",
                      isActiveLink(link.href)
                        ? "border-foreground bg-secondary/40 text-black dark:text-foreground"
                        : "border-transparent text-black/65 hover:border-border hover:bg-secondary/70 hover:text-black dark:text-muted-foreground dark:hover:text-foreground"
                    )}
                  >
                    {link.label}
                  </Link>
                ))}
                <button
                  type="button"
                  onClick={() => toggleTheme?.()}
                  className="flex w-full items-center gap-3 rounded-md border border-border bg-card px-3 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-accent"
                >
                  {theme === "light" ? (
                    <Moon className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Sun className="h-4 w-4 text-muted-foreground" />
                  )}
                  {theme === "light" ? "Dark mode" : "Light mode"}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
